/**
 * shared/components/PropertyActionBar.jsx (v2.71.67)
 * --------------------------------------------------
 * Universal, role-aware CTA bar for property detail pages.
 * Centralises the visibility logic so every dashboard renders
 * the right buttons without duplicated `isAdmin && !isTeam`
 * branches.
 *
 * ROLE MATRIX (per spec):
 *
 *   buyer/client | guest:  WhatsApp · Call · Request Callback · Save
 *   dealer       :         WhatsApp · Call · Request Callback · Save
 *   builder      :         WhatsApp · Call · Request Callback · Save
 *   team_member  :         (nothing — internal operator, see hint)
 *   admin        :         (nothing — internal operator, see hint)
 *
 * Internal operators (team_member / admin) don't see customer
 * CTAs because they don't enquire on properties they manage.
 * They use the admin/team consoles for lead management.
 *
 * Per spec: Enquiry system is REMOVED across the board — only
 * callback (1-tap → CRM lead) remains. This component therefore
 * does not render any "Send Full Enquiry" button.
 *
 * Order (per spec STEP 4):
 *   1. WhatsApp
 *   2. Call
 *   3. Request Callback
 *   4. Save Property
 */

import React from "react";
import { PhoneCall, Phone, Heart, MessageCircle, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { getPublicContactAction } from "../../lib/contactRouting";
import { useConfig } from "../../lib/ConfigContext";

export const CUSTOMER_ROLES = new Set([null, undefined, "client", "dealer", "builder"]);
export const INTERNAL_ROLES = new Set(["admin", "team_member"]);

export function shouldShowCustomerActions(role, propertyStatus) {
  if (INTERNAL_ROLES.has(role)) return false;
  if (propertyStatus === "Sold") return false;
  return true;
}

/**
 * @param role               - user's role (null for guests)
 * @param user               - logged-in user object (or null) — used for owner check + login gating
 * @param property           - the property dict
 * @param saved              - boolean — is this property saved?
 * @param savedCount         - public saved counter (optional)
 * @param savingFav          - in-flight save toggle
 * @param callbackBusy       - callback request in flight
 * @param callbackSent       - callback already requested
 * @param onCallback         - handler for Request Callback button
 * @param onToggleSave       - handler for Save toggle
 * @param onLoginRequired    - handler invoked when an anon user clicks WhatsApp / Call
 *                              (opens the auth gate / login wall)
 */
export default function PropertyActionBar({
  role,
  user,
  property,
  saved = false,
  savedCount = 0,
  savingFav = false,
  callbackBusy = false,
  callbackSent = false,
  onCallback,
  onToggleSave,
  onLoginRequired,
  className = "",
}) {
  const { content } = useConfig();
  if (!shouldShowCustomerActions(role, property?.status)) {
    return null;
  }

  // v2.87.x — Central contact routing. Public detail page CTAs MUST go
  // through this helper so owner inventory is protected (no direct tel:
  // or wa.me to dealer numbers from anon/client UI).
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const action = getPublicContactAction(property, user, content, currentUrl);
  if (action.mode === "internal_hidden") return null;
  const showLoginCta = action.mode === "login_required";

  return (
    <div
      className={`grid grid-cols-2 sm:flex sm:flex-wrap gap-2 ${className}`}
      data-testid="property-action-bar"
    >
      {/* 1 — WhatsApp / Login wall */}
      {showLoginCta ? (
        <button
          type="button"
          onClick={onLoginRequired}
          className="col-span-2 sm:col-auto inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-4 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold text-[13px] transition-all active:scale-[0.98]"
          data-testid="pab-login-required"
          aria-label="Login to contact HouseEntry"
        >
          <Lock size={14} /> {action.buttonLabel}
        </button>
      ) : action.whatsappHref ? (
        <a
          href={action.whatsappHref}
          target="_blank"
          rel="noreferrer"
          className="col-span-2 sm:col-auto inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-4 rounded-xl bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold text-[13px] transition-all active:scale-[0.98]"
          data-testid="pab-whatsapp"
        >
          <MessageCircle size={15} /> {action.buttonLabel}
        </a>
      ) : null}

      {/* 2 — Call */}
      {!showLoginCta && action.telHref ? (
        <a
          href={action.telHref}
          className="inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-4 rounded-xl border border-slate-300 bg-white text-[#09090B] hover:border-[#0055FF] hover:text-[#0055FF] font-semibold text-[13px] transition-all active:scale-[0.98]"
          data-testid="pab-call"
        >
          <Phone size={15} /> {action.callLabel}
        </a>
      ) : null}

      {/* 3 — Request Callback */}
      <Button
        onClick={onCallback}
        disabled={callbackBusy || callbackSent}
        className="h-11 sm:h-10 px-4 rounded-xl btn-brand font-semibold text-[13px]"
        data-testid="pab-callback"
      >
        <PhoneCall size={15} className="mr-1.5" />
        {callbackSent ? "Requested ✓" : callbackBusy ? "Requesting…" : "Request Callback"}
      </Button>

      {/* 4 — Save Property */}
      <Button
        variant={saved ? "default" : "outline"}
        onClick={onToggleSave}
        disabled={savingFav}
        className={`h-11 sm:h-10 px-4 rounded-xl font-semibold text-[13px] ${
          saved
            ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            : "border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]"
        }`}
        data-testid="pab-save"
      >
        <Heart size={14} className="mr-1.5" fill={saved ? "currentColor" : "none"} />
        {saved ? "Saved" : "Save"}
        {savedCount > 0 && (
          <span
            className={`ml-1.5 text-[10px] font-mono-data px-1.5 py-0.5 rounded ${
              saved ? "bg-white/20" : "bg-[#FEE2E2]"
            }`}
            data-testid="pab-save-count"
          >
            {savedCount}
          </span>
        )}
      </Button>
    </div>
  );
}
